import { Input, Modal, Radio, message } from 'antd';
import { FC, useEffect, useRef, useState } from 'react';
import { AddButton } from '../../../../../lib/components/Buttons/AddButton';
import { InputItem } from '../../../../../lib/components/InputItem';
import {
	isValidCpf,
	maskCpf,
	sanitizeCpf,
} from '../../../../../lib/utils/cpfValidator';
import { InsertSearchTagDto } from '../dto/SearchTagDto';
import { findSearchTagByCpf, useSearchTagsApi } from '../hooks/SearchTagsApi';

interface IProps {
	visible: boolean;
	close: () => void;
	insert_success: () => void;
	prefix: string;
	tag_name_in_portuguese: string;
}

const TAG_REGEX = /^[A-Za-zÀ-ÖØ-öø-ÿ0-9\s.,\/ºª°-]+$/u;
const CPF_CHECK_DEBOUNCE_MS = 400;

export const SearchTagInsertModal: FC<IProps> = ({
	visible,
	close,
	insert_success,
	prefix,
	tag_name_in_portuguese,
}) => {
	const isAuthor = prefix === 'author';
	const { insert } = useSearchTagsApi();

	const [tag_title, set_tag_title] = useState('');
	const [has_cpf, set_has_cpf] = useState(true);
	const [cpf, set_cpf] = useState('');
	const [cpf_check_loading, set_cpf_check_loading] = useState(false);
	const [cpf_conflict, set_cpf_conflict] = useState<{
		title: string;
	} | null>(null);
	const [loading, set_loading] = useState(false);
	const cpf_debounce_ref = useRef<ReturnType<typeof setTimeout> | null>(null);

	useEffect(() => {
		if (!visible) {
			set_tag_title('');
			set_has_cpf(true);
			set_cpf('');
			set_cpf_conflict(null);
			set_cpf_check_loading(false);
		}
	}, [visible]);

	useEffect(() => {
		if (cpf_debounce_ref.current) clearTimeout(cpf_debounce_ref.current);
		set_cpf_conflict(null);

		if (!isAuthor || !has_cpf) return;
		const sanitized = sanitizeCpf(cpf);
		if (sanitized.length !== 11 || !isValidCpf(sanitized)) return;

		set_cpf_check_loading(true);
		cpf_debounce_ref.current = setTimeout(async () => {
			try {
				const existing = await findSearchTagByCpf(sanitized);
				if (existing) set_cpf_conflict({ title: existing.title });
			} catch (err) {
				console.error('Erro ao verificar CPF:', err);
			} finally {
				set_cpf_check_loading(false);
			}
		}, CPF_CHECK_DEBOUNCE_MS);

		return () => {
			if (cpf_debounce_ref.current) clearTimeout(cpf_debounce_ref.current);
		};
	}, [cpf, isAuthor, has_cpf]);

	const sanitizedCpf = sanitizeCpf(cpf);
	const cpfFilled = sanitizedCpf.length > 0;
	const cpfFormatValid = sanitizedCpf.length === 11 && isValidCpf(sanitizedCpf);
	const cpfBlocking = isAuthor && has_cpf && (!cpfFormatValid || !!cpf_conflict);

	const submit = async (dto: InsertSearchTagDto) => {
		if (!dto.title) return;

		if (isAuthor) {
			const tags = [dto.title.trim()].filter((t) => t.length > 0);
			if (tags.length === 0) {
				message.warning('Digite o nome do autor.');
				return;
			}
			if (!TAG_REGEX.test(tags[0])) {
				message.error(`Nome inválido: ${tags[0]}`);
				return;
			}
			if (has_cpf) {
				if (!cpfFormatValid) {
					message.error('Informe um CPF válido.');
					return;
				}
				if (cpf_conflict) {
					message.error(
						`Já existe um autor cadastrado com este CPF: ${cpf_conflict.title}.`
					);
					return;
				}
			}

			set_loading(true);
			try {
				await insert.create({
					title: tags[0],
					prefix: dto.prefix,
					cpf: has_cpf ? sanitizedCpf : undefined,
				});
				message.success(`Autor "${tags[0]}" criado com sucesso!`);
				insert_success();
				close();
			} catch (err: any) {
				const status = err?.response?.status;
				if (status === 409) {
					const existing = err?.response?.data?.existing;
					message.error(
						`Já existe um autor cadastrado com este CPF: ${existing?.title || ''}.`
					);
				} else {
					console.error('Erro ao criar autor:', err);
					message.error('Erro ao criar autor.');
				}
			} finally {
				set_loading(false);
			}
			return;
		}

		const tags = dto.title
			.split(';')
			.map((t) => t.trim())
			.filter((t) => t.length > 0);

		if (tags.length === 0) {
			message.warning('Digite pelo menos uma tag válida.');
			return;
		}

		const invalidTags = tags.filter((tag) => !TAG_REGEX.test(tag));
		if (invalidTags.length > 0) {
			message.error(
				`As seguintes tags são inválidas: ${invalidTags.join(', ')}`
			);
			return;
		}

		set_loading(true);
		try {
			for (const tag of tags) {
				try {
					await insert.create({ title: tag, prefix: dto.prefix });
					message.success(`Tag "${tag}" criada com sucesso!`);
				} catch (err) {
					console.error(`Erro ao criar tag "${tag}":`, err);
					message.error(`Erro ao criar a tag "${tag}".`);
				}
			}
			insert_success();
			close();
		} finally {
			set_loading(false);
		}
	};

	const submitDisabled = isAuthor
		? !tag_title || cpfBlocking || cpf_check_loading || loading
		: !tag_title || loading;

	return (
		<Modal
			{...{
				visible,
				centered: true,
				closable: true,
				destroyOnClose: true,
				onCancel: () => close(),
				title: `Criar ${tag_name_in_portuguese}`,
				footer: [
					<AddButton
						{...{
							onClick: () => submit({ title: tag_title, prefix }),
							disabled: submitDisabled,
							loading,
						}}
					/>,
				],
			}}
		>
			<InputItem
				{...{
					label: isAuthor ? 'Nome do autor' : 'Título',
					on_changed: set_tag_title,
					input_item_name: tag_title,
				}}
			/>

			{isAuthor ? (
				<>
					<div className='mt-4'>
						<label className='mr-4 font-semibold'>
							Você possui o CPF do autor que deseja cadastrar?
						</label>
						<div className='mt-2'>
							<Radio.Group
								value={has_cpf ? 'yes' : 'no'}
								onChange={(e) => set_has_cpf(e.target.value === 'yes')}
							>
								<Radio value='yes'>Sim</Radio>
								<Radio value='no'>Não</Radio>
							</Radio.Group>
						</div>
					</div>

					{has_cpf && (
						<div className='mt-4'>
							<label className='mr-4 font-semibold'>
								<span className='text-red-500'> * </span>CPF:
							</label>
							<div className='w-5/6'>
								<Input
									{...{
										value: cpf,
										onChange: (event) => set_cpf(maskCpf(event.target.value)),
										placeholder: '000.000.000-00',
										maxLength: 14,
										allowClear: true,
									}}
								/>
							</div>
							<div className='mt-2 text-sm'>
								{cpf_check_loading && (
									<span style={{ color: '#888' }}>Verificando CPF...</span>
								)}
								{!cpf_check_loading && cpfFilled && !cpfFormatValid && (
									<span style={{ color: '#d4380d' }}>CPF inválido.</span>
								)}
								{!cpf_check_loading && cpf_conflict && (
									<span style={{ color: '#d4380d' }}>
										Já existe um autor cadastrado com este CPF:{' '}
										{cpf_conflict.title}.
									</span>
								)}
								{!cpf_check_loading &&
									cpfFormatValid &&
									!cpf_conflict && (
										<span style={{ color: '#52c41a' }}>CPF disponível.</span>
									)}
							</div>
						</div>
					)}
				</>
			) : (
				<small style={{ color: '#888' }}>
					Digite várias tags usando apenas <b>letras</b> e separando com{' '}
					<b>ponto e vírgula(;)</b>.<br />
					Ex: Autor(es): <i>João Souza;Maria dos Santos;José da Silva...</i>
					<br />
					Palavra(s)-chave: <i>Administrativo;Crimes eleitorais;Licitações...</i>
				</small>
			)}
		</Modal>
	);
};
